/**
 * BPF Memorial Renderer — Milestone 3 (Builder-identical, scene-included
 * export).
 *
 * Long-term rendering platform, not a one-off export utility. Its whole
 * job is:
 *
 *   canvasState -> resolveLayerPlan() -> render memorial -> export frame -> PNG
 *
 * It knows nothing about Shopify, uploads, App Proxy, carts, orders, or
 * line-item properties, and never will — this file must stay a pure
 * consumer of a canvasState object (the exact shape assets/bpf-
 * memorial-designer.js's getCanvasState() already produces) and a
 * plain-object `options`, returning a canvas or a Blob. It also has no
 * concept of Builder layout or DOM positioning beyond reading the
 * already-resolved asset URLs out of canvasState/resolveLayerPlan() —
 * this is deliberately NOT "screenshot the Builder canvas"; every pixel
 * here is drawn from source assets via this file's own compositing,
 * independent of whatever the on-screen canvas happens to be showing at
 * the moment.
 *
 * Milestone 3 restores the Builder's actual on-screen composition after
 * Milestone 2's transparent, memorial-only detour: stage background,
 * then the memorial, in the SAME 4:3 frame shape
 * .bpf-memorial-designer__canvas-placeholder already uses (CSS
 * `aspect-ratio: 4/3`). This is a restoration, not a redesign — the
 * frame shape and per-layer fit rule (cover for stage, contain for
 * everything else) are the same conventions this file has always
 * replicated; only the export target briefly diverged from them in
 * Milestone 2, at explicit product direction.
 *
 * The stage image is taken as `options.stageSrc`, supplied by the
 * caller (currently the temporary QA trigger, assets/bpf-memorial-
 * export-qa.js) reading it live off the already-rendered on-screen
 * `.bpf-memorial-canvas__stage` <img>'s own resolved `.src` — never a
 * hardcoded filename anywhere in this file. Whatever asset the Builder
 * is actually using today (a .jpg at the time of writing, though that
 * is deliberately not this file's concern) is what gets drawn; a future
 * asset swap in sections/bpf-memorial-designer.liquid requires zero
 * change here.
 *
 * Zero duplicated rendering logic, by construction:
 *   - Layer/color resolution: window.BpfMemorialCanvasRenderer.
 *     resolveLayerPlan() (assets/bpf-memorial-canvas.js) — the exact
 *     same pure decision the on-screen Builder canvas uses, extracted
 *     there specifically so this file never needed its own copy.
 *   - Engraving: window.BpfVaultEngravingLayer (assets/bpf-vault-
 *     engraving-layer.js) — completely unmodified, constructed against
 *     a NEW intermediate canvas (see renderEngravedLayer() below), never
 *     against the export canvas directly. That class draws the engraved
 *     surface via a full-canvas stretch, which is only correct when its
 *     target canvas already matches that asset's own native aspect
 *     ratio — true for the intermediate canvas (sized from the loaded
 *     base image's own naturalWidth/naturalHeight, never a second
 *     hardcoded copy of that number), false for the export frame
 *     itself (4:3, but not necessarily at the base asset's own exact
 *     pixel resolution). The intermediate canvas's finished output is
 *     then composited onto the real export frame via the SAME
 *     drawContain() primitive used for every other layer — a canvas is
 *     just another drawImage() source. This is exactly the Milestone 2
 *     fix, unchanged: it is what makes restoring the stage layer safe
 *     without reintroducing the original Milestone 1 bug (engraving's
 *     internal ctx.clearRect() silently wiping a stage already drawn
 *     onto the same canvas) — the two layers never share a canvas until
 *     the already-finished, already-blended engraved result is
 *     `drawContain()`-composited on top of the already-drawn stage.
 *
 * Export frame: one configuration object (EXPORT_FRAME_DEFAULTS below)
 * is the single source of truth for the default 2000x1500 (4:3) size —
 * nothing else in this file hardcodes those numbers. Callers override
 * via options.width/options.height (a future print-quality or
 * thumbnail caller asks for different dimensions; nothing about the
 * rendering logic itself changes, and the aspect ratio a caller
 * requests is that caller's own choice, not enforced here).
 *
 * Future extension seam (not implemented): drawContain()'s `placement`
 * parameter is always undefined today, since no per-asset x/y/scale
 * metadata exists anywhere in this codebase (confirmed against
 * snippets/bpf-memorial-visual-mapping.liquid's actual schema — every
 * color entry is only ever `{"src": "..."}`). If that ever changes, a
 * future caller threads the new metadata through this exact parameter;
 * the compositing loop and every call site keep their current shape.
 *
 * CORS: every layer asset is a Shopify theme asset (asset_url), served
 * from the same CDN already proven to send
 * Access-Control-Allow-Origin: * (Phase 5.3.2B investigation, documented
 * in assets/bpf-vault-engraving-layer.js) — every image load here sets
 * crossOrigin = 'anonymous' before src, the same proven pattern, so the
 * resulting canvas is never tainted and toBlob()/toDataURL() work.
 *
 * Determinism: every image load awaits decode() (not just onload —
 * decode() guarantees the bitmap is actually ready to draw, not just
 * that a response arrived) and, before any engraving draw, this file
 * awaits the engraving typeface via document.fonts.load()/.ready — a
 * gap that exists nowhere else in the engraving pipeline today (ctx.font
 * is set with no readiness check anywhere else), closed here rather
 * than inside BpfVaultEngravingLayer/BpfEngravingShadowRenderer to keep
 * this file's touch to those proven, customer-facing files to zero.
 *
 * What this file does NOT include, deliberately: the status overlay
 * (data-canvas-status) is an error/unavailable MESSAGE, not artwork —
 * an unresolved or unavailable canvasState simply produces a stage-only
 * (or, with no stageSrc supplied, blank) canvas here, matching
 * BpfMemorialCanvasRenderer's own neutral "nothing overlaid" state.
 */

if (typeof window.BpfMemorialRenderer === 'undefined') {
  window.BpfMemorialRenderer = (function () {
    'use strict';

    // The one configuration source for the export frame's default size
    // — every other reference to these numbers in this file reads from
    // here, never a second literal. 4:3 matches
    // .bpf-memorial-designer__canvas-placeholder's own CSS aspect-ratio.
    const EXPORT_FRAME_DEFAULTS = Object.freeze({ width: 2000, height: 1500 });

    // Preload strategy: same "never touch anything visible before the
    // load is confirmed" discipline BpfMemorialCanvasRenderer._applyLayer()
    // already uses, tightened with decode() since this is a one-shot
    // export with no later render() to self-correct a half-decoded
    // frame the way continuous on-screen updates effectively can.
    function loadImage(src) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
          if (typeof img.decode === 'function') {
            img
              .decode()
              .then(() => resolve(img))
              .catch(() => resolve(img)); // decode() rejecting still leaves an onload-confirmed image safe to draw
          } else {
            resolve(img);
          }
        };
        img.onerror = () => reject(new Error('BpfMemorialRenderer: failed to load image "' + src + '"'));
        img.src = src;
      });
    }

    // A canvas and an image expose their pixel dimensions under
    // different property names — this is the only place drawContain()/
    // drawCover() need to know that, so both can accept either kind of
    // source.
    function getSourceDimensions(source) {
      if (typeof HTMLCanvasElement !== 'undefined' && source instanceof HTMLCanvasElement) {
        return { width: source.width, height: source.height };
      }
      return { width: source.naturalWidth, height: source.naturalHeight };
    }

    // CSS object-fit: contain, replicated for canvas drawImage() —
    // matches every .bpf-memorial-canvas__layer's existing CSS
    // (object-fit: contain) exactly, so an export frame at a different
    // aspect ratio than a given asset's own native size still
    // letterboxes/centers the same way the on-screen composition
    // already does. Accepts an HTMLImageElement OR an HTMLCanvasElement
    // as `source` — the latter is how the engraved surface's
    // intermediate canvas (see renderEngravedLayer()) gets composited
    // onto the real export frame.
    //
    // `placement` is the documented, currently-unused future extension
    // seam for per-asset positioning metadata — see module header.
    // Always undefined today; when undefined, the only behavior that
    // exists is applied (center, scaled to fit entirely within `frame`).
    // Pure placement math, no drawing — the shared core both drawContain()
    // (draws + returns it) and compositePictureFramePhoto() below (needs
    // the FRAME asset's own placement without drawing it a second time —
    // see that function's own comment) read from.
    function containRect(sw, sh, frame) {
      const scale = Math.min(frame.width / sw, frame.height / sh);
      const drawW = sw * scale;
      const drawH = sh * scale;
      return {
        x: frame.x + (frame.width - drawW) / 2,
        y: frame.y + (frame.height - drawH) / 2,
        width: drawW,
        height: drawH,
      };
    }

    // Phase 4D.10C.1: `cropNormalized` ({x,y,width,height}, each 0-1
    // relative to source's OWN native size) is an optional 5th param —
    // every pre-existing call site omits it and is completely unaffected
    // (undefined -> the original whole-image behavior). Added
    // specifically for the Picture Frame placeholder assets, which are
    // NOT tightly-cropped photo-shaped files — see
    // placeholderContentBounds's own comment in sections/bpf-memorial-
    // designer.liquid for why a real customer photo never needs this.
    function drawContain(ctx, source, frame, placement, cropNormalized) {
      const { width: sw, height: sh } = getSourceDimensions(source);
      if (cropNormalized) {
        const cropX = cropNormalized.x * sw;
        const cropY = cropNormalized.y * sh;
        const cropW = cropNormalized.width * sw;
        const cropH = cropNormalized.height * sh;
        const rect = containRect(cropW, cropH, frame);
        ctx.drawImage(source, cropX, cropY, cropW, cropH, rect.x, rect.y, rect.width, rect.height);
        return rect;
      }
      const rect = containRect(sw, sh, frame);
      ctx.drawImage(source, rect.x, rect.y, rect.width, rect.height);
      // Phase 4D.10C: the placement this call actually used, returned
      // (not just applied) — every pre-existing call site discards this
      // return value (unchanged, harmless).
      return rect;
    }

    // CSS object-fit: cover, replicated the same way — used only for
    // the stage background, matching .bpf-memorial-canvas__stage's own
    // CSS (object-fit: cover). Restored from Milestone 1; Milestone 2
    // deleted it entirely along with the stage layer it existed for.
    function drawCover(ctx, source, frame) {
      const { width: sw, height: sh } = getSourceDimensions(source);
      const scale = Math.max(frame.width / sw, frame.height / sh);
      const drawW = sw * scale;
      const drawH = sh * scale;
      const dx = frame.x + (frame.width - drawW) / 2;
      const dy = frame.y + (frame.height - drawH) / 2;
      ctx.save();
      ctx.beginPath();
      ctx.rect(frame.x, frame.y, frame.width, frame.height);
      ctx.clip();
      ctx.drawImage(source, dx, dy, drawW, drawH);
      ctx.restore();
    }

    // Phase 4D.10C, Parts 22/26/27 — the ONE shared Picture Frame
    // compositing primitive, used unchanged by BOTH the live preview
    // (assets/bpf-memorial-canvas.js, drawing onto a fixed-2000x1500
    // <canvas> scaled to its box via CSS) and this file's own PNG export
    // (drawing onto the real 2000x1500 export frame) — "prefer one
    // shared/config-driven contract" from the spec, satisfied literally:
    // one function, one call site shape, two different ctx targets.
    //
    // Deliberately does NOT draw the frame artwork itself (frameImg) —
    // only computes where it WOULD land (containRect, the same math
    // drawContain() uses) so the aperture can be placed correctly
    // relative to that rectangle. Live mode never draws the frame onto
    // this same canvas at all (a separate <img> handles it, CSS
    // object-fit: contain producing the identical placement); export
    // mode's caller draws the frame via a normal drawContain(ctx,
    // frameImg, frameBox) call immediately before this one, satisfying
    // Part 26's "frame first, photo on top" ordering on a single canvas.
    //
    // apertureNormalized is Portrait/Landscape's own {x,y,width,height},
    // each 0-1 relative to the FRAME ASSET's own image — not the outer
    // frameBox — which is exactly why frameImg's own native dimensions
    // (via getSourceDimensions) are needed here, not just frameBox.
    //
    // The actual photo/placeholder draw is a normal drawContain() into
    // the computed aperture rect — no crop, proportional, centered
    // (Part 27) — reusing the exact same primitive as every other layer
    // in this file, not a new fit algorithm.
    // photoCropNormalized (optional, 0-1 relative to photoImg's own
    // native size): passed through unchanged to drawContain() — see that
    // function's own comment. Only ever supplied for the placeholder
    // path (its content occupies a small sub-rectangle of a much larger
    // mostly-transparent canvas); a real customer photo omits this
    // entirely, since customer photos already ARE tightly-cropped,
    // photo-shaped files with no extra padding to strip.
    function compositePictureFramePhoto(ctx, frameImg, frameBox, apertureNormalized, photoImg, photoCropNormalized) {
      const { width: fw, height: fh } = getSourceDimensions(frameImg);
      const frameRect = containRect(fw, fh, frameBox);
      const apertureRect = {
        x: frameRect.x + apertureNormalized.x * frameRect.width,
        y: frameRect.y + apertureNormalized.y * frameRect.height,
        width: apertureNormalized.width * frameRect.width,
        height: apertureNormalized.height * frameRect.height,
      };
      drawContain(ctx, photoImg, apertureRect, undefined, photoCropNormalized);
      return apertureRect;
    }

    // Reads the same shared [data-engraving-data] script tag
    // BpfVaultEngravingLayer._ensureData() already reads — duplicated as
    // a two-line lookup ONLY because this file needs to know the
    // engraving typeface BEFORE calling that class, to await its
    // readiness (see module header). The actual layout/drawing logic
    // that consumes this data is never re-implemented here.
    function readEngravingData() {
      const script = document.querySelector('[data-engraving-data]');
      if (!script) return null;
      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        return null;
      }
    }

    async function ensureEngravingFontReady(styleProfile) {
      if (!styleProfile || typeof document === 'undefined' || !document.fonts) return;
      const weight = styleProfile.fontWeight && styleProfile.fontWeight !== 'normal' ? styleProfile.fontWeight + ' ' : '';
      // The exact size drawn doesn't matter for a font-face load — any
      // size string is enough to trigger the browser fetching/decoding
      // the right face; document.fonts.ready below is what actually
      // guarantees every requested face is settled before drawing.
      const fontString = weight + '48px ' + (styleProfile.fontFamily || 'sans-serif');
      try {
        await document.fonts.load(fontString);
        await document.fonts.ready;
      } catch (error) {
        // Never block export on a font-loading failure — ctx.font's own
        // system-font fallback is the same graceful degradation the
        // rest of this pipeline already relies on elsewhere.
      }
    }

    // Renders the engraved surface exactly as BpfVaultEngravingLayer
    // already does — completely unmodified, same class, same
    // render(canvasState) contract — onto a NEW intermediate canvas
    // sized to `referenceImg`'s own native resolution (never a second
    // copy of a "1160x870"-style constant; naturalWidth/naturalHeight
    // off the already-loaded image is the one source of truth for that
    // number). That class's internal full-canvas stretch is only
    // correct when its target canvas already matches the redrawn
    // asset's own aspect ratio, which this intermediate canvas
    // guarantees by construction.
    //
    // Surface-agnostic (Dog Bone Vault fix): BpfVaultEngravingLayer
    // itself decides whether to redraw the base or the figure asset,
    // via canvasState.visualAssets.engravingSurface — this function
    // doesn't need to know which; `referenceImg` only needs to be
    // *a* same-aspect-ratio image so the intermediate canvas is sized
    // sanely (the caller passes whichever of base/figure it already
    // loaded for that surface — see renderMemorialPreviewCanvas()).
    //
    // Returns the intermediate canvas (already containing the fully
    // rendered, correctly blended surface) on success, or null if
    // engraving failed/is unavailable for any reason — the caller falls
    // back to drawing the plain image directly, the same graceful
    // degradation this pipeline has always had.
    async function renderEngravedLayer(referenceImg, canvasState) {
      if (typeof window.BpfVaultEngravingLayer === 'undefined') return null;
      const intermediate = document.createElement('canvas');
      intermediate.width = referenceImg.naturalWidth;
      intermediate.height = referenceImg.naturalHeight;

      try {
        const engravingData = readEngravingData();
        await ensureEngravingFontReady(engravingData ? engravingData.styleProfile : null);
        const layer = new window.BpfVaultEngravingLayer(intermediate);
        const engraved = await layer.render(canvasState);
        return engraved ? intermediate : null;
      } catch (error) {
        return null;
      }
    }

    // Phase 4D.10C, Parts 26/30 — draws the selected Picture Frame ON TOP
    // of whatever the scene/Vault/Capsule drawing above this call already
    // put on `ctx` (Part 26's locked composition order), directly onto
    // the SAME export frame — unlike the live preview's separate
    // <canvas data-canvas-frame-photo> overlay, there is only one canvas
    // here, so this draws the frame artwork itself first via a normal
    // drawContain(), then the effective photo on top via
    // compositePictureFramePhoto(). A complete no-op (frame OFF, or any
    // failure) leaves whatever was already drawn untouched — same
    // per-layer error isolation as every other asset in this function,
    // never aborts the export.
    //
    // effective photo (Part 24): the customer's own uploaded File/Blob
    // when one exists, else the orientation-specific Builder placeholder
    // — this function only ever READS canvasState.uploadedPhoto, it never
    // writes to selectedPhotoFile or any canonical photo state.
    async function drawPictureFrameLayer(ctx, canvasState, frame) {
      if (!canvasState || !canvasState.pictureFrameSelected) return;
      const profiles = canvasState.pictureFrameProfiles;
      if (!profiles) return;

      const orientationKey = (canvasState.pictureFrameOrientation || 'Portrait').toLowerCase();
      const profile = profiles[orientationKey];
      if (!profile) return;

      const frameSrc = profile.standardAssets && profile.standardAssets[canvasState.pictureFrameColor];
      if (!frameSrc) return;

      const hasCustomerPhoto = Boolean(canvasState.uploadedPhoto);
      const placeholderSrc = profile.placeholderAsset;
      if (!hasCustomerPhoto && !placeholderSrc) return;

      let photoObjectUrl = null;
      try {
        const framePromise = loadImage(frameSrc);
        const photoSrcPromise = hasCustomerPhoto
          ? Promise.resolve().then(() => {
              photoObjectUrl = URL.createObjectURL(canvasState.uploadedPhoto);
              return loadImage(photoObjectUrl);
            })
          : loadImage(placeholderSrc);

        const [frameImg, photoImg] = await Promise.all([framePromise, photoSrcPromise]);
        drawContain(ctx, frameImg, frame);
        // Phase 4D.10C.1: the placeholder's own visible content is a
        // small sub-rectangle of a much larger canvas — see
        // placeholderContentBounds's comment in sections/bpf-memorial-
        // designer.liquid. Only ever applied on the placeholder path;
        // a real customer photo is passed through uncropped.
        const photoCrop = hasCustomerPhoto ? undefined : profile.placeholderContentBounds;
        compositePictureFramePhoto(ctx, frameImg, frame, profile.aperture, photoImg, photoCrop);
      } catch (error) {
        // Frame or photo/placeholder failed to load — the scene/Memorial
        // drawn above this call still stands; never abort the export
        // over the add-on layer.
      } finally {
        if (photoObjectUrl) {
          try {
            URL.revokeObjectURL(photoObjectUrl);
          } catch (error) {
            // best-effort cleanup only
          }
        }
      }
    }

    /**
     * Renders exactly what the Memorial Canvas shows — stage, Vault
     * (base + figure, with engraving replacing the plain base when a
     * name exists) or Capsule, nothing else — onto a new offscreen
     * canvas, in the same 4:3 frame shape and per-layer fit rule the
     * Builder's own CSS already uses.
     *
     * @param {object} canvasState - the exact shape getCanvasState() in
     *   assets/bpf-memorial-designer.js already produces.
     * @param {object} [options]
     * @param {number} [options.width=2000] - see EXPORT_FRAME_DEFAULTS.
     * @param {number} [options.height=1500] - see EXPORT_FRAME_DEFAULTS.
     * @param {string} [options.stageSrc] - the Builder's own live stage
     *   image URL (read by the caller off the on-screen
     *   .bpf-memorial-canvas__stage <img>'s .src); omit to render
     *   without a background layer.
     * @returns {Promise<HTMLCanvasElement>}
     */
    async function renderMemorialPreviewCanvas(canvasState, options) {
      const opts = options || {};
      const width = opts.width || EXPORT_FRAME_DEFAULTS.width;
      const height = opts.height || EXPORT_FRAME_DEFAULTS.height;
      const frame = { x: 0, y: 0, width, height };

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, width, height);

      // Phase 4D.9B.5, Part 10 (Wall = Yes canvas-export latency): the
      // stage background and the Vault/Capsule asset(s) below have no
      // data dependency on each other — only each asset's OWN engraving
      // step depends on THAT asset's own decoded naturalWidth/
      // naturalHeight, never on a different asset's load. Previously
      // these were awaited one at a time (stage, then base, then
      // figure), stacking up to three sequential CDN round trips before
      // the canvas was even ready to draw. Starting the stage load here,
      // immediately, and starting base/figure/capsule as soon as `plan`
      // is known below (before awaiting any of them) lets their network/
      // decode time overlap; drawing itself still happens in the
      // original stage -> base -> figure z-order, and each layer's own
      // load failure still never blocks the others (same try/catch-per-
      // layer isolation as before).
      const stageImgPromise = opts.stageSrc ? loadImage(opts.stageSrc) : null;

      // Unresolved, or resolved with no matching visual record: the
      // neutral state, identical in spirit to BpfMemorialCanvasRenderer's
      // own "static stage alone, nothing overlaid" branch.
      const hasVisualAssets = Boolean(canvasState && canvasState.isResolved && canvasState.visualAssets);
      const canResolvePlan =
        hasVisualAssets &&
        typeof window.BpfMemorialCanvasRenderer !== 'undefined' &&
        typeof window.BpfMemorialCanvasRenderer.resolveLayerPlan === 'function';
      const plan = canResolvePlan ? window.BpfMemorialCanvasRenderer.resolveLayerPlan(canvasState.visualAssets, canvasState) : null;

      if (plan && plan.compositionMode === 'flattened') {
        // Dog Bone Capsule extension: mirrors the Vault branch below —
        // most Capsules have no engraving at all (engravingSurface
        // defaults to "base", which is meaningless for a flattened
        // composition and simply never matches "capsule" here); Dog
        // Bone Capsule's own record sets engravingSurface: "capsule",
        // the one signal that both its Builder canvas and this export
        // path use to decide whether to redraw the flattened image with
        // engraved text instead of drawing it plain.
        const capsuleImgPromise = plan.capsule && plan.capsule.src ? loadImage(plan.capsule.src) : null;

        if (stageImgPromise) {
          try {
            drawCover(ctx, await stageImgPromise, frame);
          } catch (error) {
            // Stage is decorative background — leave the frame blank rather than aborting the export.
          }
        }

        if (capsuleImgPromise) {
          try {
            const capsuleImg = await capsuleImgPromise;
            const capsuleEngravingSurface = (canvasState.visualAssets && canvasState.visualAssets.engravingSurface) || 'base';
            const wantsCapsuleEngraving =
              canvasState.memorialStyle === 'Capsule' &&
              capsuleEngravingSurface === 'capsule' &&
              Boolean(canvasState.engravedName) &&
              typeof window.BpfVaultEngravingLayer !== 'undefined';
            if (wantsCapsuleEngraving) {
              const engravedCapsule = await renderEngravedLayer(capsuleImg, canvasState);
              drawContain(ctx, engravedCapsule || capsuleImg, frame);
            } else {
              drawContain(ctx, capsuleImg, frame);
            }
          } catch (error) {
            // Unmapped/failed capsule asset — stage-only, matching
            // BpfMemorialCanvasRenderer's own "nothing overlaid" look
            // for a color the resolver doesn't recognize.
          }
        }
        await drawPictureFrameLayer(ctx, canvasState, frame);
        return canvas;
      }

      // Vault: figure is drawn highest, base (or its engraved
      // replacement) below it — same z-order .bpf-memorial-canvas__base/
      // __figure already enforce via CSS.
      //
      // Dog Bone Vault fix: which of base/figure is "or its engraved
      // replacement" depends on canvasState.visualAssets.engravingSurface
      // — "base" (every existing record's default) engraves the base
      // exactly as before; "figure" (Dog Bone Vault only, whose
      // engravable surface is physically the bone-shaped figure, not
      // the pedestal beneath it) engraves the figure instead, and the
      // base is always drawn plain in that case. Never both — the two
      // branches below are mutually exclusive per engravingSurface, so
      // the plain, un-engraved counterpart is never drawn on top of the
      // engraved one.
      const baseImgPromise = plan && plan.base && plan.base.src ? loadImage(plan.base.src) : null;
      const figureImgPromise = plan && plan.figure && plan.figure.src ? loadImage(plan.figure.src) : null;

      if (stageImgPromise) {
        try {
          drawCover(ctx, await stageImgPromise, frame);
        } catch (error) {
          // Stage is decorative background — leave the frame blank rather than aborting the export.
        }
      }

      if (!hasVisualAssets || !canResolvePlan) {
        // Phase 4D.10C: the Picture Frame is independent of Memorial
        // product resolution — an unresolved/no-visual-assets Memorial
        // must not suppress an already-selected frame.
        await drawPictureFrameLayer(ctx, canvasState, frame);
        return canvas;
      }

      const engravingSurface = (canvasState.visualAssets && canvasState.visualAssets.engravingSurface) || 'base';
      const wantsEngraving =
        canvasState.memorialStyle === 'Vault' &&
        Boolean(canvasState.engravedName) &&
        typeof window.BpfVaultEngravingLayer !== 'undefined';

      if (baseImgPromise) {
        try {
          const baseImg = await baseImgPromise;
          if (wantsEngraving && engravingSurface === 'base') {
            const engravedBase = await renderEngravedLayer(baseImg, canvasState);
            drawContain(ctx, engravedBase || baseImg, frame);
          } else {
            drawContain(ctx, baseImg, frame);
          }
        } catch (error) {
          // Base asset missing/failed — stage alone rather than aborting the whole export.
        }
      }

      if (figureImgPromise) {
        try {
          const figureImg = await figureImgPromise;
          if (wantsEngraving && engravingSurface === 'figure') {
            const engravedFigure = await renderEngravedLayer(figureImg, canvasState);
            drawContain(ctx, engravedFigure || figureImg, frame);
          } else {
            drawContain(ctx, figureImg, frame);
          }
        } catch (error) {
          // Figure asset missing/failed — base/engraving still renders correctly alone.
        }
      }

      await drawPictureFrameLayer(ctx, canvasState, frame);
      return canvas;
    }

    /**
     * Same rendering as renderMemorialPreviewCanvas(), returned as a
     * PNG Blob — the return type a future upload step needs verbatim
     * (a fetch body / FormData entry, no re-encoding), and what the
     * temporary QA trigger uses via URL.createObjectURL().
     *
     * @param {object} canvasState
     * @param {object} [options] - same as renderMemorialPreviewCanvas()
     * @returns {Promise<Blob>}
     */
    async function exportMemorialPreviewPNG(canvasState, options) {
      const canvas = await renderMemorialPreviewCanvas(canvasState, options);
      return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
          else reject(new Error('BpfMemorialRenderer: canvas.toBlob() returned null'));
        }, 'image/png');
      });
    }

    return {
      renderMemorialPreviewCanvas,
      exportMemorialPreviewPNG,
      EXPORT_FRAME_DEFAULTS,
      // Phase 4D.10C: exported so assets/bpf-memorial-canvas.js's live
      // preview can reuse the identical image-loading and Picture Frame
      // compositing primitives at RUNTIME (after every deferred script
      // has loaded — the same kind of forward reference that file
      // already makes to window.BpfVaultEngravingLayer, itself defined
      // in a script that loads even later than this one) rather than
      // maintaining a second copy of any of this.
      loadImage,
      drawContain,
      getSourceDimensions,
      compositePictureFramePhoto,
    };
  })();
}
